import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { usersAPI, groupsAPI, ousAPI, gpoAPI } from '../services/api';

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Helper to check if cache is valid
const isCacheValid = (lastFetch) => {
  if (!lastFetch) return false;
  return Date.now() - lastFetch < CACHE_DURATION;
};

// Async thunks for fetching data
export const fetchUsers = createAsyncThunk(
  'data/fetchUsers',
  async (force = false, { getState }) => {
    const state = getState();
    if (!force && isCacheValid(state.data.users.lastFetch)) {
      return { cached: true };
    }
    const response = await usersAPI.list();
    return { data: response.data.results || [], cached: false };
  }
);

export const fetchGroups = createAsyncThunk(
  'data/fetchGroups',
  async (force = false, { getState }) => {
    const state = getState();
    if (!force && isCacheValid(state.data.groups.lastFetch)) {
      return { cached: true };
    }
    const response = await groupsAPI.list();
    return { data: response.data.results || [], cached: false };
  }
);

export const fetchOUs = createAsyncThunk(
  'data/fetchOUs',
  async (force = false, { getState }) => {
    const state = getState();
    if (!force && isCacheValid(state.data.ous.lastFetch)) {
      return { cached: true };
    }
    const response = await ousAPI.list();
    return { data: response.data.results || [], cached: false };
  }
);

export const fetchGPOs = createAsyncThunk(
  'data/fetchGPOs',
  async (force = false, { getState }) => {
    const state = getState();
    if (!force && isCacheValid(state.data.gpos.lastFetch)) {
      return { cached: true };
    }
    const response = await gpoAPI.list();
    return { data: response.data.results || [], cached: false };
  }
);

// Async thunk for fetching group members
export const fetchGroupMembers = createAsyncThunk(
  'data/fetchGroupMembers',
  async ({ groupName, force = false }, { getState }) => {
    const state = getState();
    const cachedMembers = state.data.groupMembers[groupName];

    if (!force && cachedMembers && isCacheValid(cachedMembers.lastFetch)) {
      return { groupName, cached: true };
    }

    const response = await groupsAPI.getMembers(groupName);
    return {
      groupName,
      data: response.data.members || [],
      cached: false
    };
  }
);

const initialState = {
  users: {
    data: [],
    loading: false,
    error: null,
    lastFetch: null,
    // Which domain controller served this data (not which one is selected).
    source: null,
  },
  groups: {
    data: [],
    loading: false,
    error: null,
    lastFetch: null,
    // Which domain controller served this data (not which one is selected).
    source: null,
  },
  ous: {
    data: [],
    loading: false,
    error: null,
    lastFetch: null,
    // Which domain controller served this data (not which one is selected).
    source: null,
  },
  gpos: {
    data: [],
    loading: false,
    error: null,
    lastFetch: null,
    // Which domain controller served this data (not which one is selected).
    source: null,
  },
  groupMembers: {
    // Structure: { [groupName]: { data: [], lastFetch: timestamp } }
  },
};

const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    clearCache: (state) => {
      state.users.lastFetch = null;
      state.groups.lastFetch = null;
      state.ous.lastFetch = null;
      state.gpos.lastFetch = null;
      state.groupMembers = {};
    },
    clearUserCache: (state) => {
      state.users.lastFetch = null;
    },
    clearGroupCache: (state) => {
      state.groups.lastFetch = null;
      state.groupMembers = {};
    },
    clearOUCache: (state) => {
      state.ous.lastFetch = null;
    },
    clearGPOCache: (state) => {
      state.gpos.lastFetch = null;
    },
    // Optimistic updates for immediate UI response
    addUserOptimistic: (state, action) => {
      state.users.data.push(action.payload);
    },
    updateUserOptimistic: (state, action) => {
      const index = state.users.data.findIndex(u => u.username === action.payload.username);
      if (index !== -1) {
        state.users.data[index] = { ...state.users.data[index], ...action.payload };
      }
    },
    removeUserOptimistic: (state, action) => {
      state.users.data = state.users.data.filter(u => u.username !== action.payload);
    },
    addGroupOptimistic: (state, action) => {
      state.groups.data.push(action.payload);
    },
    removeGroupOptimistic: (state, action) => {
      state.groups.data = state.groups.data.filter(g => g.name !== action.payload);
    },
    addOUOptimistic: (state, action) => {
      state.ous.data.push(action.payload);
    },
    removeOUOptimistic: (state, action) => {
      state.ous.data = state.ous.data.filter(ou => ou.dn !== action.payload);
    },
    addGPOOptimistic: (state, action) => {
      state.gpos.data.push(action.payload);
    },
    removeGPOOptimistic: (state, action) => {
      state.gpos.data = state.gpos.data.filter(gpo => gpo.name !== action.payload);
    },
  },
  extraReducers: (builder) => {
    // Users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.users.loading = true;
        state.users.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.users.loading = false;
        if (!action.payload.cached) {
          state.users.data = action.payload.data;
          state.users.source = action.payload.source;
          state.users.lastFetch = Date.now();
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.users.loading = false;
        state.users.error = action.error.message;
      });

    // Groups
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.groups.loading = true;
        state.groups.error = null;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.groups.loading = false;
        if (!action.payload.cached) {
          state.groups.data = action.payload.data;
          state.groups.source = action.payload.source;
          state.groups.lastFetch = Date.now();
        }
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.groups.loading = false;
        state.groups.error = action.error.message;
      });

    // OUs
    builder
      .addCase(fetchOUs.pending, (state) => {
        state.ous.loading = true;
        state.ous.error = null;
      })
      .addCase(fetchOUs.fulfilled, (state, action) => {
        state.ous.loading = false;
        if (!action.payload.cached) {
          state.ous.data = action.payload.data;
          state.ous.source = action.payload.source;
          state.ous.lastFetch = Date.now();
        }
      })
      .addCase(fetchOUs.rejected, (state, action) => {
        state.ous.loading = false;
        state.ous.error = action.error.message;
      });

    // GPOs
    builder
      .addCase(fetchGPOs.pending, (state) => {
        state.gpos.loading = true;
        state.gpos.error = null;
      })
      .addCase(fetchGPOs.fulfilled, (state, action) => {
        state.gpos.loading = false;
        if (!action.payload.cached) {
          state.gpos.data = action.payload.data;
          state.gpos.source = action.payload.source;
          state.gpos.lastFetch = Date.now();
        }
      })
      .addCase(fetchGPOs.rejected, (state, action) => {
        state.gpos.loading = false;
        state.gpos.error = action.error.message;
      });

    // Group Members
    builder
      .addCase(fetchGroupMembers.fulfilled, (state, action) => {
        if (!action.payload.cached) {
          state.groupMembers[action.payload.groupName] = {
            data: action.payload.data,
            lastFetch: Date.now(),
          };
        }
      });
  },
});

export const {
  clearCache,
  clearUserCache,
  clearGroupCache,
  clearOUCache,
  clearGPOCache,
  addUserOptimistic,
  updateUserOptimistic,
  removeUserOptimistic,
  addGroupOptimistic,
  removeGroupOptimistic,
  addOUOptimistic,
  removeOUOptimistic,
  addGPOOptimistic,
  removeGPOOptimistic,
} = dataSlice.actions;

export default dataSlice.reducer;
